// Players tab of the upload page: hide or show each player on the board, one setting for everyone.
// Hiding keeps every number; the board just leaves the player out (upload groups can still "show hidden" there).
import React, { useEffect, useState } from "react";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import { headshotUrl, Palette } from "./reboundingModel";
import { PlayerSetting } from "./types";
import { errorText, head14, Notice, num, pill, who, when } from "./ui";

const COLS = "54px minmax(220px,320px) 90px 240px minmax(160px,1fr)";

const PlayersTab: React.FC<{ P: Palette }> = ({ P }) => {
  const api = useLeagueApi();
  const [players, setPlayers] = useState<PlayerSetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [noPhoto, setNoPhoto] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    api.getReboundingPlayers().then((d) => setPlayers(d.players)).catch((e) => setError(errorText(e)));
  }, [api]);

  const toggle = async (p: PlayerSetting, hidden: boolean) => {
    if (p.hidden === hidden) return;
    setSaving(p.playerId);
    setError(null);
    try {
      const res = await api.setReboundingPlayerHidden(p.playerId, hidden);
      setPlayers((ps) => ps?.map((x) => (x.playerId === p.playerId ? { ...x, ...res } : x)) ?? ps);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(null);
    }
  };

  const hiddenCount = players?.filter((p) => p.hidden).length ?? 0;

  return (
    <div>
      <div style={{ fontSize: 17, color: P.sub, margin: "0 0 12px" }}>
        Hidden players keep all their numbers; they just don't appear on the board, for anyone.
        {players ? ` ${hiddenCount} hidden.` : ""}
      </div>
      {error && <Notice P={P} tone="bad">{error}</Notice>}
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: "max-content" }}>
          <div style={{ display: "grid", gridTemplateColumns: COLS, columnGap: 14, height: 30, alignItems: "end", paddingBottom: 5, borderBottom: `2px solid ${P.headRule}`, ...head14(P) }}>
            <span />
            <span>PLAYER</span>
            <span style={{ textAlign: "right" }}>SESSIONS</span>
            <span>ON THE BOARD</span>
            <span>CHANGED</span>
          </div>
          {players?.map((p, i) => {
            const photo = !noPhoto.has(p.nbaId) ? headshotUrl(p.nbaId) : null;
            return (
              <div key={p.playerId} style={{ display: "grid", gridTemplateColumns: COLS, columnGap: 14, minHeight: 50, alignItems: "center", borderBottom: `1px solid ${P.rule}`, background: i % 2 ? P.zebra : "transparent", margin: "0 -10px", padding: "0 10px" }}>
                <div style={{ width: 54, height: 40, background: P.photoBg, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", opacity: p.hidden ? 0.4 : 1 }}>
                  {photo ? (
                    <img src={photo} alt="" onError={() => setNoPhoto((s) => new Set(s).add(p.nbaId))} style={{ width: 54, height: 40, objectFit: "cover", objectPosition: "top" }} />
                  ) : (
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: P.photoInk }}>{p.first.slice(0, 2)}</span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0, opacity: p.hidden ? 0.4 : 1 }}>
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 30, lineHeight: 1, whiteSpace: "nowrap" }}>{p.first}</span>
                  <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: ".1em", color: P.mute, whiteSpace: "nowrap" }}>{p.last}</span>
                  {!p.onRoster && <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", color: P.sub }}>OFF ROSTER</span>}
                </div>
                <span style={{ ...num, fontSize: 22, textAlign: "right" }}>{p.sessions || "—"}</span>
                <div style={{ display: "flex" }} role="radiogroup" aria-label={`${p.fullName} on the board`}>
                  <button role="radio" aria-checked={!p.hidden} disabled={saving === p.playerId} onClick={() => toggle(p, false)} style={{ ...pill(P, !p.hidden, 15), height: 30 }}>
                    SHOWN
                  </button>
                  <button role="radio" aria-checked={p.hidden} disabled={saving === p.playerId} onClick={() => toggle(p, true)} style={{ ...pill(P, p.hidden, 15), height: 30 }}>
                    HIDDEN
                  </button>
                </div>
                <span style={{ fontSize: 14, color: P.sub }}>
                  {saving === p.playerId ? "Saving…" : p.updatedBy ? `${who(p.updatedBy)} · ${when(p.updatedAt)}` : ""}
                </span>
              </div>
            );
          })}
          {!players && !error && <div style={{ padding: "12px 0", fontSize: 17, color: P.sub }}>Loading…</div>}
        </div>
      </div>
    </div>
  );
};

export default PlayersTab;
